import { escapeHtmlText } from "./bookTemplate";
import { assertNever } from "../lib/assertNever";
import { isJust, just, matchMaybe, nothing, type Maybe } from "../lib/maybe";
import { sceneCustomName } from "../lib/sceneLabel";
import { type ZwChapter, type ZwSceneFormatting } from "../lib/zwConfig";

/** The literal marker text for a break style, or Nothing when no marker
 * element should be inserted at all (plain paragraph break only). */
export function sceneBreakMarkerText(
  formatting: ZwSceneFormatting,
): Maybe<string> {
  switch (formatting.breakStyle) {
    case "none":
      return nothing;
    case "blank":
      return just("");
    case "asterisk":
      return just("* * *");
    case "hash":
      return just("#");
    case "custom": {
      const trimmed = formatting.breakCustomText.trim();
      return trimmed.length !== 0 ? just(trimmed) : nothing;
    }
    default:
      return assertNever(formatting.breakStyle);
  }
}

/**
 * Joins a chapter's scene HTML (`zwdocToExportHtml`) into one chapter body,
 * inserting scene-title headings and/or a scene-break marker between scenes
 * per project settings. 1-scene chapters are untouched, matching the
 * editor's own "hide hierarchy until 2+ scenes" convention.
 */
export function assembleChapterHtml(
  chapter: ZwChapter,
  sceneBodies: string[],
  formatting: ZwSceneFormatting,
): string {
  const trimmed = sceneBodies.map((body) => body.trim());
  if (trimmed.length <= 1) return trimmed[0] ?? "";

  const marker = sceneBreakMarkerText(formatting);
  const parts: string[] = [];
  trimmed.forEach((body, index) => {
    if (index > 0 && isJust(marker)) {
      parts.push(
        `<p class="zw-scene-break">${escapeHtmlText(marker.value)}</p>`,
      );
    }
    // Nameless scenes get no heading (just the break); only a name the
    // writer typed is ever printed, never a positional "Scene N".
    const heading = formatting.showSceneTitles
      ? matchMaybe(sceneCustomName(chapter.scenes[index]!), {
          just: (name) =>
            `<h2 class="scene-title">${escapeHtmlText(name)}</h2>\n\n`,
          nothing: () => "",
        })
      : "";
    parts.push(`${heading}${body}`);
  });
  return parts.join("\n\n");
}
